import { DebugWidget, createWidgetContainer } from '../DebugOverlay';
import type { FloorIsolationManager } from './FloorIsolationManager';
import type { FloorIsolationStrategy, IsolationSpec } from './FloorIsolationController';

export class FloorIsolationDebugWidget implements DebugWidget {
  name = 'floor-isolation';
  container: HTMLElement;
  private readonly getManager: () => FloorIsolationManager | undefined;
  private readonly statusEl: HTMLElement;
  private readonly modeEl: HTMLElement;
  private readonly floorIdEl: HTMLElement;
  private readonly strategyEl: HTMLElement;
  private readonly valuesEl: HTMLElement;

  constructor(getManager: () => FloorIsolationManager | undefined) {
    this.getManager = getManager;
    this.container = createWidgetContainer('🏠 Floor Isolation');

    const content = document.createElement('div');
    content.style.cssText = 'line-height: 1.6;';
    content.innerHTML = `
      <div><strong>Active:</strong> <span id="debug-fi-status">--</span></div>
      <div><strong>Mode:</strong> <span id="debug-fi-mode">--</span></div>
      <div><strong>Floor ID:</strong> <span id="debug-fi-floor-id" style="font-family: monospace; font-size: 11px;">--</span></div>
      <div><strong>Strategy:</strong> <span id="debug-fi-strategy">--</span></div>
      <div style="margin-top: 4px; padding-left: 8px; font-size: 11px; color: rgba(255, 255, 255, 0.7);" id="debug-fi-values">--</div>
    `;

    this.container.appendChild(content);

    this.statusEl = queryEl(content, '#debug-fi-status');
    this.modeEl = queryEl(content, '#debug-fi-mode');
    this.floorIdEl = queryEl(content, '#debug-fi-floor-id');
    this.strategyEl = queryEl(content, '#debug-fi-strategy');
    this.valuesEl = queryEl(content, '#debug-fi-values');
  }

  update(): void {
    const manager = this.getManager();
    if (!manager) {
      this.statusEl.textContent = 'manager not ready';
      return;
    }

    const spec = manager.getActiveSpec();
    const isActive = spec !== null;
    const floorId = manager.getActiveFloorGroupId();

    this.statusEl.textContent = isActive ? (manager.isFrozen() ? '❄️ Frozen' : '✅ On') : '❌ Off';
    this.statusEl.style.color = isActive ? '#4CAF50' : 'rgba(255, 255, 255, 0.6)';

    this.modeEl.textContent = manager.isInCustomMode() ? 'Custom (tool-driven)' : 'Property-driven';
    this.floorIdEl.textContent = floorId ?? '--';

    if (spec) {
      this.strategyEl.textContent = describeSpec(spec);
      this.valuesEl.innerHTML = formatSpecValues(spec);
    } else {
      this.strategyEl.textContent = '--';
      this.valuesEl.textContent = '--';
    }
  }

  destroy(): void {
    // No cleanup needed
  }
}

function describeSpec(spec: IsolationSpec): string {
  const base = spec.base ? spec.base.kind : 'none';
  const overrides = spec.overrides ? Object.keys(spec.overrides).length : 0;
  return overrides ? `${base} (+${overrides} view override${overrides > 1 ? 's' : ''})` : base;
}

function formatSpecValues(spec: IsolationSpec): string {
  const parts = [`<u>base</u>: ${formatStrategy(spec.base)}`];
  if (spec.overrides) {
    for (const [viewId, strategy] of Object.entries(spec.overrides)) {
      parts.push(`<u>${viewId}</u>: ${formatStrategy(strategy)}`);
    }
  }
  return parts.join('<br>');
}

function formatStrategy(strategy: FloorIsolationStrategy | null): string {
  if (!strategy) return 'exempt (no clip)';
  switch (strategy.kind) {
    case 'fixedHeight':
      return `fixedHeight ${strategy.height.toFixed(2)} m`;
    case 'roomBounds': {
      const margin = strategy.margin ?? 0;
      return `roomBounds ${strategy.floorHeight.toFixed(2)}→${strategy.ceilingHeight.toFixed(2)} m (margin ${margin.toFixed(2)})`;
    }
    case 'box':
      return `box z ${strategy.minZ.toFixed(2)}→${strategy.maxZ.toFixed(2)} m`;
  }
}

function queryEl<T extends HTMLElement>(parent: HTMLElement, selector: string): T {
  const el = parent.querySelector<T>(selector);
  if (!el) {
    throw new Error(`Element not found: ${selector}`);
  }
  return el;
}
